import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import UniTrustSEO from "@/components/app/UniTrustSEO";
import {
  ArrowLeft,
  ExternalLink,
  ShieldCheck,
  Loader2,
  Link as LinkIcon,
  MessageCircle,
  Linkedin,
  Check,
} from "lucide-react";

/**
 * /uni/:id/trust — public provenance receipt.
 *
 * Renders every audited `field_edits` row for the university and
 * its degrees, newest-first, in a shareable table. Owners can send
 * this URL to marketing or press as "here is exactly what has been
 * verified about our institution on WUA".
 *
 * Data source: GET /api/universities/{id}/trust-log (public,
 * unauthenticated, actor emails redacted).
 */
export default function UniTrust() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(
          `/universities/${id}/trust-log?limit=500`,
        );
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load trust log");
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <div className="font-editorial text-xl">Trust log unavailable</div>
        <p className="mt-2 text-sm text-stone-600">{err}</p>
        <Link to={`/uni/${id}`} className="mt-6 inline-block">
          <Button variant="outline">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to university
          </Button>
        </Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2 text-stone-500" />
        <span className="label-uppercase inline">Loading provenance…</span>
      </div>
    );
  }

  const items = data.items || [];

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `Verified provenance receipt for ${data.university_name} on WUA`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Public URL copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} — ${shareUrl}`)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <div
      className="max-w-5xl mx-auto px-6 py-10"
      data-testid="uni-trust-page"
    >
      <UniTrustSEO
        universityId={id}
        universityName={data.university_name}
        officialDomains={data.official_domains || []}
        totalRows={data.total || 0}
      />
      <Link to={`/uni/${id}`} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Portal
      </Link>

      <TooltipProvider delayDuration={150}>
        <div
          className="mt-4 flex items-center gap-1.5"
          data-testid="uni-trust-share-row"
          role="group"
          aria-label="Share this provenance receipt"
        >
          <span className="mr-1 text-[10px] font-underground-sc tracking-widest text-stone-500">
            Share
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full border-stone-300 hover:bg-stone-50"
                onClick={copyLink}
                data-testid="uni-trust-share-copy"
                aria-label="Copy public URL"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-emerald-700" />
                ) : (
                  <LinkIcon className="h-4 w-4 text-stone-700" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {copied ? "Copied!" : "Copy public URL"}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full border-stone-300 hover:bg-emerald-50 hover:border-emerald-300"
              >
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="uni-trust-share-whatsapp"
                  aria-label="Share on WhatsApp"
                >
                  <MessageCircle className="h-4 w-4 text-emerald-700" />
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Share on WhatsApp</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full border-stone-300 hover:bg-blue-50 hover:border-blue-300"
              >
                <a
                  href={linkedinHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="uni-trust-share-linkedin"
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="h-4 w-4 text-blue-700" />
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Share on LinkedIn</TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      <header className="mt-3">
        <div className="flex items-center gap-2 text-emerald-800">
          <ShieldCheck className="h-4 w-4" />
          <span className="text-[10px] font-underground-sc tracking-widest">
            Provenance receipt
          </span>
        </div>
        <h1 className="mt-1 font-editorial text-3xl sm:text-4xl">
          What has been verified about {data.university_name}
        </h1>
        <p className="mt-2 text-sm text-stone-600 max-w-2xl">
          A full audit trail of every data-field edit on this
          university's WUA record — who touched what, when, and where
          the source came from. Deep-linkable and safe to share.
        </p>
        <div className="mt-3 text-[11px] font-mono text-stone-500">
          {data.total.toLocaleString()} audit rows · showing newest{" "}
          {items.length}
        </div>
      </header>

      {items.length === 0 ? (
        <div
          className="mt-10 p-8 border border-dashed border-stone-300 rounded-xl text-center text-stone-500"
          data-testid="uni-trust-empty"
        >
          No verified edits yet — either the university is a fresh
          record or all edits pre-date the Field Registry audit trail.
        </div>
      ) : (
        <div
          className="mt-8 rounded-xl border border-stone-200 overflow-hidden bg-white"
          data-testid="uni-trust-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-[10px] font-underground-sc tracking-widest text-stone-500">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Where</th>
                <th className="text-left px-4 py-2">Field</th>
                <th className="text-left px-4 py-2">Change</th>
                <th className="text-left px-4 py-2">By</th>
                <th className="text-left px-4 py-2">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {items.map((r, i) => (
                <tr
                  key={r.id || i}
                  className="hover:bg-stone-50/70 align-top"
                  data-testid={`uni-trust-row-${i}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-[11px] font-mono text-stone-500">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {r.collection === "universities" ? (
                      <span className="text-[10px] font-underground-sc tracking-widest text-stone-500">
                        University
                      </span>
                    ) : (
                      <div className="text-[13px] text-stone-800 min-w-[160px] max-w-[280px]">
                        <div className="truncate">{r.doc_title || "Course"}</div>
                        {r.doc_qualification && (
                          <div className="text-[10px] font-mono text-stone-400">
                            {r.doc_qualification}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[13px] text-stone-900 font-medium">
                      {r.field_label || r.path}
                    </div>
                    {r.section && (
                      <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                        {r.section}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[220px] max-w-[360px]">
                    <div className="text-[12px] font-mono text-stone-500 truncate">
                      {r.before === null || r.before === undefined
                        ? <span className="text-stone-400 italic">empty</span>
                        : String(r.before)}
                    </div>
                    <div className="text-[13px] font-mono text-emerald-800 truncate">
                      → {r.after === null || r.after === undefined
                        ? <span className="italic">empty</span>
                        : String(r.after)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-[12px] text-stone-800">
                      {r.actor_name || (r.actor_type === "university" ? "University" : "WUA")}
                    </div>
                    <div className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                      {r.actor_type === "university" ? "by university" : "by WUA"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {r.source_url ? (
                      <a
                        href={r.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[12px] text-purple-700 hover:underline max-w-[240px] truncate"
                        data-testid={`uni-trust-source-${i}`}
                      >
                        <ExternalLink className="h-3 w-3 shrink-0" />
                        <span className="truncate">{r.source_url.replace(/^https?:\/\//, "")}</span>
                      </a>
                    ) : (
                      <span className="text-stone-400 text-[11px]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-6 text-[11px] font-underground-sc tracking-wider text-stone-400">
        Actor email addresses are redacted from this public view.
        Full receipts including reviewer contact live inside the
        Uni Portal audit log.
      </p>
    </div>
  );
}
